import {announce} from '@react-aria/live-announcer'

import cartPlusSvg from '@/assets/icons/cart-plus.svg'
import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import {ADD_TO_CART_EVENT} from '@/constants/events'
import useAddToOrRemoveFromCartMutation from '@/lib/cart/hooks/useAddToOrRemoveFromCartMutation'
import {type SupportedLocales, useLocale} from '@/lib/locale/locale'
import {useSetCustomItems} from '@/lib/payment/store'
import type {MultiFullProduct, Product} from '@/models/productModel'
import {PaymentRoute} from '@/routeRegistry'
import {getProductDetails} from '@/services/productService'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import toastErrorMessage from '@/utils/errors/toastErrorMessage'
import {logEvent} from '@/utils/logger'

import style from './ProductCard.module.scss'

export function getVariationFromProductDetails(
  product: Product,
  productDetails?: MultiFullProduct['products'][number],
) {
  return (
    productDetails?.variations.find(variation => variation.id === product.id)?.values.entries() ??
    productDetails?.variations.at(0)?.values.entries()
  )
}

async function getVariationAndDetails(
  locale: SupportedLocales,
  product: Product,
  queryClient: QueryClient,
) {
  const productDetails = await getProductDetails({
    locale,
    id: product.id,
    retailer: product.retailer,
    minimal: true,
    queryClient,
  })

  if (!productDetails || productDetails.products.length === 0) {
    return {
      variation: undefined,
      productDetails: undefined,
    }
  }

  const variationEntries = getVariationFromProductDetails(product, productDetails.products[0])

  return {
    variation: variationEntries ? Object.fromEntries(variationEntries) : undefined,
    productDetails: productDetails.products[0],
  }
}

interface CartActionsProps {
  product: Product
}

export default memo<CartActionsProps>(function ProductCardActions({product}) {
  const latestProduct = useLatest(product)
  const queryClient = useQueryClient()
  const [isProcessingAddingToCart, setIsProcessingAddingToCart] = useState(false)
  const latestIsProcessing = useLatest(isProcessingAddingToCart)
  const {mutate: addToOrRemoveFromCart, status: addToOrRemoveFromCartStatus} =
    useAddToOrRemoveFromCartMutation()
  const latestaddToOrRemoveFromCartStatus = useLatest(addToOrRemoveFromCartStatus)
  const [locale] = useLocale()

  const onAddProduct = useCallback(async () => {
    if (latestaddToOrRemoveFromCartStatus.current === 'pending' || latestIsProcessing.current)
      return

    logEvent(...ADD_TO_CART_EVENT)

    announce('Adding the product to the cart', 'assertive')

    setIsProcessingAddingToCart(true)

    const {variation, productDetails} = await getVariationAndDetails(
      locale,
      latestProduct.current,
      queryClient,
    )
    const price = productDetails?.price

    setIsProcessingAddingToCart(false)

    if (typeof price !== 'number' || Number.isNaN(price)) {
      const errorMsg = 'Unexpected error, please try again later!'
      toastErrorMessage(errorMsg)
      return
    }

    addToOrRemoveFromCart({
      items: [
        {
          ...latestProduct.current,
          price,
          quantity: 1,
          variation,
        },
      ],
    })
  }, [locale, addToOrRemoveFromCart, queryClient])

  const isAddingToCart = isProcessingAddingToCart || addToOrRemoveFromCartStatus === 'pending'

  //----------------------------------------------------------------------------

  const navigate = useNavigate()
  const setCustomItems = useSetCustomItems()

  const [isBuying, setIsBuying] = useState(false)
  const latestIsBuying = useLatest(isBuying)

  const onBuyNow = useCallback(async () => {
    logEvent(...ADD_TO_CART_EVENT)
    if (latestIsBuying.current) return

    setIsBuying(true)
    const {variation, productDetails} = await getVariationAndDetails(
      locale,
      latestProduct.current,
      queryClient,
    )
    const price = productDetails?.price

    if (typeof price !== 'number' || Number.isNaN(price)) {
      const errorMsg = 'Unexpected error, please try again later!'
      toastErrorMessage(errorMsg)
      setIsBuying(false)
      return
    }

    setCustomItems([
      {
        ...latestProduct.current,
        price,
        quantity: 1,
        variation,
      },
    ])

    setIsBuying(false)

    await navigate({
      to: PaymentRoute.fullPath,
      search: {
        custom: true,
      },
    })
  }, [navigate, locale, queryClient, setCustomItems])

  return (
    <div className={style.CardActions}>
      {!isBuying && (
        <PrimaryButton
          className={style.AddToCartBtn}
          onPress={onAddProduct}
          isPending={isAddingToCart}
          pendingIndicatorClassName={style.Loader}
        >
          {!isAddingToCart && <img src={cartPlusSvg} alt='' aria-hidden='true' />}
          <VisuallyHidden>
            {isAddingToCart
              ? `Adding to cart ${latestProduct.current.title}`
              : `Add to cart ${latestProduct.current.title}`}
          </VisuallyHidden>
        </PrimaryButton>
      )}
      <PrimaryButton className={style.BuyNowBtn} isPending={isBuying} onPress={onBuyNow}>
        {!isBuying ? 'Buy now' : 'Buying'}
        <VisuallyHidden>{latestProduct.current.title}</VisuallyHidden>
      </PrimaryButton>
    </div>
  )
})
